import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Warehouse, Palette, Wand2, Upload, FileText, X } from "lucide-react";
import api, { apiErr, startAndPollMenu, startAndPollImport } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import GenerationOverlay from "@/components/GenerationOverlay";
import { PageHeader } from "@/components/kit";

const THEMES = ["Valentine\u2019s Dinner", "Modern Italian", "New Orleans Brunch", "Luxury Steakhouse",
  "Summer Rooftop", "Vegan Tasting Menu", "Japanese Fusion", "Private Dinner", "Wedding", "Holiday Dinner"];
const SERVICE = ["Fine Dining", "Casual Dining", "Fast Casual", "Catering", "Private Chef", "Banquet", "Buffet", "Tasting Menu", "Pop-Up", "Food Truck"];
const SAMPLE = "Create a five-course modern Southern tasting menu for 80 guests. The experience should feel luxurious, soulful and contemporary. Use short rib, sweet potatoes, bourbon and collard greens where appropriate. Target food cost is 28%.";

const MODES = [
  { id: "vibe", label: "From a Vibe", icon: Sparkles },
  { id: "kitchen", label: "From My Kitchen", icon: Warehouse },
  { id: "theme", label: "From a Theme", icon: Palette },
  { id: "import", label: "Import My Recipe", icon: Upload },
];

export default function Create() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") || "vibe");
  const [f, setF] = useState({
    prompt: params.get("q") || "", theme: "", ingredients: "", courses: 3, guests: 2,
    cuisine: "", avoid: "", allergies: "", dietary: "", target_food_cost: user?.profile?.target_food_cost || 28,
    menu_price: "", service_style: "Fine Dining", skill_level: "professional", prep_time: "",
  });
  const [active, setActive] = useState(false);
  const [doneMenu, setDoneMenu] = useState(null);
  const [imp, setImp] = useState({ text: "", file: null, scale: "", target: user?.profile?.target_food_cost || 28 });
  const upd = (k, v) => setF({ ...f, [k]: v });
  const maxCourses = { chef: 3, sous_chef: 5, executive_chef: 7, master_chef: 7, admin: 7 }[user?.plan] || 3;

  useEffect(() => { if (params.get("mode")) setMode(params.get("mode")); }, [params]);

  const generate = async (demo = false) => {
    setActive(true); setDoneMenu(null);
    try {
      const body = {
        mode, demo,
        prompt: f.prompt, theme: mode === "theme" ? f.theme : undefined,
        ingredients: f.ingredients ? f.ingredients.split(",").map((s) => s.trim()).filter(Boolean) : [],
        courses: +f.courses, guests: +f.guests, cuisine: f.cuisine || undefined,
        vibe: f.prompt || undefined,
        avoid: f.avoid ? f.avoid.split(",").map((s) => s.trim()) : [],
        allergies: f.allergies ? f.allergies.split(",").map((s) => s.trim()) : [],
        dietary: f.dietary ? f.dietary.split(",").map((s) => s.trim()) : [],
        target_food_cost: +f.target_food_cost, menu_price: f.menu_price ? +f.menu_price : undefined,
        service_style: f.service_style, skill_level: f.skill_level, prep_time: f.prep_time || undefined,
      };
      const menu = await startAndPollMenu(body);
      setDoneMenu(menu);
    } catch (e) {
      setActive(false);
      toast.error(apiErr(e));
    }
  };

  const importMenu = async () => {
    if (!imp.text.trim() && !imp.file) { toast.error("Paste your recipe/menu or attach a file first."); return; }
    setActive(true); setDoneMenu(null);
    try {
      const fd = new FormData();
      if (imp.file) fd.append("file", imp.file);
      if (imp.text.trim()) fd.append("text", imp.text.trim());
      if (imp.scale) fd.append("scale_servings", String(imp.scale));
      fd.append("target_food_cost", String(imp.target || 28));
      fd.append("service_style", f.service_style);
      const menu = await startAndPollImport(fd);
      setDoneMenu(menu);
    } catch (e) {
      setActive(false);
      toast.error(apiErr(e));
    }
  };

  return (
    <div>
      <PageHeader eyebrow="Create" title="Compose a menu"
        subtitle="Set the brief. The Chef Plated AI Engine builds a coherent, professional menu — course by course."
        action={<button onClick={() => generate(true)} data-testid="load-demo-btn" className="btn-outline-gold px-6 py-3 text-xs uppercase tracking-widest rounded-sm">Load Demo Menu</button>} />

      {/* mode tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {MODES.map((m) => (
          <button key={m.id} onClick={() => setMode(m.id)} data-testid={`mode-${m.id}`}
            className={`flex items-center gap-2 px-5 py-3 rounded-sm text-sm border transition-colors min-h-[48px]
            ${mode === m.id ? "border-gold bg-gold/10 text-gold" : "border-white/10 text-white/60 hover:border-white/25"}`}>
            <m.icon size={16} /> {m.label}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {mode === "import" ? (
          <div className="lg:col-span-3 grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 matte-card p-6 space-y-5" data-testid="import-panel">
              <div>
                <label className="label-eyebrow block mb-2">Paste your recipe or menu</label>
                <textarea rows={7} value={imp.text} onChange={(e) => setImp({ ...imp, text: e.target.value })} data-testid="import-text"
                  placeholder={"Paste one dish or a full menu.\n\ne.g. Braised Short Rib — serves 4. Ingredients: 2 lb short rib, 1 onion, 2 cups red wine, beef stock, carrots, thyme. Method: sear, braise 3 hrs..."}
                  className="w-full bg-surface border border-white/10 rounded-sm px-4 py-3.5 text-white/90 outline-none focus:border-gold/60 resize-none" />
              </div>
              <div className="flex items-center gap-3 text-white/30 text-xs uppercase tracking-widest">
                <span className="h-px flex-1 bg-white/10" /> or attach a file <span className="h-px flex-1 bg-white/10" />
              </div>
              <div>
                <label className="label-eyebrow block mb-2">Upload a photo, PDF or document</label>
                {!imp.file ? (
                  <label data-testid="import-file-label"
                    className="flex flex-col items-center justify-center gap-2 border border-dashed border-white/15 rounded-sm py-8 cursor-pointer hover:border-gold/50 transition-colors">
                    <Upload size={22} className="text-gold/70" />
                    <span className="text-white/60 text-sm">Click to upload a recipe photo, PDF, DOCX, TXT, CSV or XLSX</span>
                    <span className="text-white/30 text-xs">Max 15MB</span>
                    <input type="file" className="hidden" data-testid="import-file-input"
                      accept=".jpg,.jpeg,.png,.webp,.gif,.pdf,.docx,.txt,.csv,.xlsx"
                      onChange={(e) => setImp({ ...imp, file: e.target.files?.[0] || null })} />
                  </label>
                ) : (
                  <div className="flex items-center justify-between gap-3 border border-white/15 rounded-sm px-4 py-3" data-testid="import-file-chip">
                    <span className="flex items-center gap-2 text-white/80 text-sm min-w-0"><FileText size={16} className="text-gold shrink-0" /> <span className="truncate">{imp.file.name}</span></span>
                    <button onClick={() => setImp({ ...imp, file: null })} data-testid="import-file-remove" className="text-white/40 hover:text-white shrink-0"><X size={16} /></button>
                  </div>
                )}
              </div>
              <p className="text-white/40 text-sm">We'll structure it into courses, refine the details, cost every plate, and generate plating visuals automatically.</p>
            </div>
            <div className="matte-card p-6 space-y-5 h-fit">
              <Field label="Scale to servings (optional)" type="number" value={imp.scale} onChange={(e) => setImp({ ...imp, scale: e.target.value })} testid="import-scale" placeholder="e.g. 20" />
              <Field label="Target food cost %" type="number" value={imp.target} onChange={(e) => setImp({ ...imp, target: e.target.value })} testid="import-target" />
              <div>
                <label className="label-eyebrow block mb-2">Service style</label>
                <select value={f.service_style} onChange={(e) => upd("service_style", e.target.value)} data-testid="import-service"
                  className="w-full bg-surface border border-white/10 rounded-sm px-4 py-3.5 text-white/90 outline-none focus:border-gold/60">
                  {SERVICE.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <button onClick={importMenu} data-testid="import-menu-btn"
                className="btn-gold w-full py-4 text-sm uppercase tracking-widest rounded-sm flex items-center justify-center gap-2">
                <Upload size={16} /> Import & Build
              </button>
              <p className="text-white/30 text-xs text-center">AI-structured. Verify food-safety, allergens & pricing before service.</p>
            </div>
          </div>
        ) : (
        <>
        <div className="lg:col-span-2 matte-card p-6 space-y-5">
          {mode === "vibe" && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="label-eyebrow">Describe the experience</label>
                <button onClick={() => upd("prompt", SAMPLE)} className="text-gold text-xs hover:underline flex items-center gap-1"><Wand2 size={12} /> Use sample</button>
              </div>
              <textarea rows={4} value={f.prompt} onChange={(e) => upd("prompt", e.target.value)} data-testid="create-prompt"
                placeholder="Upscale Southern dinner inspired by fall in Georgia. Elegant, warm, soulful and modern."
                className="w-full bg-surface border border-white/10 rounded-sm px-4 py-3.5 text-white/90 outline-none focus:border-gold/60 resize-none" />
            </div>
          )}
          {mode === "kitchen" && (
            <div>
              <label className="label-eyebrow block mb-2">Ingredients on hand (comma separated)</label>
              <textarea rows={3} value={f.ingredients} onChange={(e) => upd("ingredients", e.target.value)} data-testid="create-ingredients"
                placeholder="salmon, asparagus, potatoes, lemon, butter"
                className="w-full bg-surface border border-white/10 rounded-sm px-4 py-3.5 text-white/90 outline-none focus:border-gold/60 resize-none" />
              <textarea rows={2} value={f.prompt} onChange={(e) => upd("prompt", e.target.value)}
                placeholder="Optional: any direction for the dishes…"
                className="mt-3 w-full bg-surface border border-white/10 rounded-sm px-4 py-3.5 text-white/90 outline-none focus:border-gold/60 resize-none" />
            </div>
          )}
          {mode === "theme" && (
            <div>
              <label className="label-eyebrow block mb-2">Choose a theme</label>
              <div className="flex flex-wrap gap-2">
                {THEMES.map((t) => (
                  <button key={t} onClick={() => upd("theme", t)} data-testid={`theme-${t.split(" ")[0].toLowerCase()}`}
                    className={`px-4 py-2.5 rounded-sm text-sm border transition-colors ${f.theme === t ? "border-gold bg-gold/10 text-gold" : "border-white/10 text-white/60 hover:border-white/25"}`}>{t}</button>
                ))}
              </div>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Cuisine" value={f.cuisine} onChange={(e) => upd("cuisine", e.target.value)} testid="create-cuisine" placeholder="Modern Southern" />
            <div>
              <label className="label-eyebrow block mb-2">Service style</label>
              <select value={f.service_style} onChange={(e) => upd("service_style", e.target.value)} data-testid="create-service"
                className="w-full bg-surface border border-white/10 rounded-sm px-4 py-3.5 text-white/90 outline-none focus:border-gold/60">
                {SERVICE.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="Ingredients to avoid" value={f.avoid} onChange={(e) => upd("avoid", e.target.value)} testid="create-avoid" placeholder="pork, shellfish" />
            <Field label="Allergies" value={f.allergies} onChange={(e) => upd("allergies", e.target.value)} testid="create-allergies" placeholder="peanut" />
            <Field label="Dietary" value={f.dietary} onChange={(e) => upd("dietary", e.target.value)} testid="create-dietary" placeholder="vegetarian" />
          </div>
        </div>

        {/* Config panel */}
        <div className="matte-card p-6 space-y-5 h-fit">
          <div>
            <label className="label-eyebrow block mb-3">Number of courses</label>
            <div className="flex flex-wrap gap-2">
              {[1, 2, 3, 4, 5, 6, 7].map((n) => {
                const locked = n > maxCourses;
                return (
                  <button key={n} disabled={locked} onClick={() => upd("courses", n)} data-testid={`courses-${n}`}
                    className={`h-11 w-11 rounded-sm text-sm font-mono border transition-colors
                    ${f.courses === n ? "border-gold bg-gold/10 text-gold" : locked ? "border-white/5 text-white/20 cursor-not-allowed" : "border-white/10 text-white/60 hover:border-white/25"}`}>{n}</button>
                );
              })}
            </div>
            {maxCourses < 7 && <p className="text-white/30 text-xs mt-2">Up to {maxCourses} on your plan.</p>}
          </div>
          <Field label="Guests / covers" type="number" value={f.guests} onChange={(e) => upd("guests", e.target.value)} testid="create-guests" />
          <div className="grid grid-cols-2 gap-4">
            <Field label="Target food cost %" type="number" value={f.target_food_cost} onChange={(e) => upd("target_food_cost", e.target.value)} testid="create-target" />
            <Field label="Menu price $" type="number" value={f.menu_price} onChange={(e) => upd("menu_price", e.target.value)} testid="create-price" placeholder="opt." />
          </div>
          <button onClick={() => generate(false)} data-testid="generate-menu-btn"
            className="btn-gold w-full py-4 text-sm uppercase tracking-widest rounded-sm flex items-center justify-center gap-2">
            <Sparkles size={16} /> Generate Menu
          </button>
          <p className="text-white/30 text-xs text-center">AI-generated. Verify food-safety, allergens & pricing before service.</p>
        </div>
        </>
        )}
      </div>

      <GenerationOverlay active={active} done={!!doneMenu} menuName={doneMenu?.menu_name}
        onReveal={() => nav(`/menus/${doneMenu.id}`)} />
    </div>
  );
}

function Field({ label, type = "text", value, onChange, testid, placeholder }) {
  return (
    <div>
      <label className="label-eyebrow block mb-2">{label}</label>
      <input type={type} value={value} onChange={onChange} data-testid={testid} placeholder={placeholder}
        className="w-full bg-surface border border-white/10 rounded-sm px-4 py-3.5 text-white/90 outline-none focus:border-gold/60 placeholder:text-white/25" />
    </div>
  );
}
